// @flow
import _ from 'lodash';
import {
  DEFAULT_SEARCH_SETTINGS,
  FIELDS_NOT_SUPPORT_IN_V1,
  SEARCH_FIELDS_TITLE,
  SEARCH_VERSION
} from 'src/constants/search-settings';
import { SUBSCRIPTION_STATE } from 'src/constants/subscription';

/**
 * Build initial data for search fields
 *
 * @param  {Object} parseParams parse params from API
 *
 * @return {Object} initialValue
 */
export const buildInitialSearchSettingsValues = (searchSettings, usingNewTheme = false) => {
  let result = {};

  const {
    searchFields = [],
    minimumMustMatch = {},
    inCollectionSearch = {},
    fuzzySearch = 'true'
  } = searchSettings;
  //Hide fields in old settings
  let searchFieldsValue = DEFAULT_SEARCH_SETTINGS.searchFields
    .filter(e => !FIELDS_NOT_SUPPORT_IN_V1.includes(e.name))
    .map(e => ({
      name: e.name,
      status: e.status
    }));
  if (_.isEmpty(searchFields)) {
    // Default values
    result.searchFields = searchFieldsValue;
  } else {
    if (searchFields.length !== searchFieldsValue.length) {
      // Update old search field to new search settings
      searchFields.map(item => {
        let indexSearchField = _.findIndex(searchFieldsValue, { name: item.name });
        if (indexSearchField && indexSearchField >= 0) {
          searchFieldsValue[indexSearchField] = item;
        }
        return '';
      });
      result.searchFields = searchFieldsValue;
    } else {
      // Search fields array is already in new format
      result.searchFields = searchFields;
    }
  }

  if (usingNewTheme) {
    result.inCollectionSearch = {
      ...DEFAULT_SEARCH_SETTINGS.inCollectionSearch,
      ...inCollectionSearch
    };
  }

  result.minimumMustMatch = { ...DEFAULT_SEARCH_SETTINGS.minimumMustMatch, ...minimumMustMatch };

  result.fuzzySearch = [fuzzySearch];

  return result;
};

/**
 * Build initial data for search fields v2
 *
 * @param  {Object} parseParams parse params from API
 *
 * @return {Object} initialValue
 */
export const buildInitialSearchSettingsV2Values = (searchSettings, usingNewTheme = false) => {
  let result = {};

  const {
    searchFields = [],
    minimumMustMatch = {},
    inCollectionSearch = {},
    fuzzySearch = 'true'
  } = searchSettings;
  let searchFieldsValue = [...DEFAULT_SEARCH_SETTINGS.searchFields];
  if (_.isEmpty(searchFields)) {
    // Default values
    result.searchFields = searchFieldsValue;
  } else {
    // Update old search field to new search settings
    const searchFieldsSetting = searchFieldsValue.map(item => {
      let indexSearchField = _.findIndex(searchFields, { name: item.name });
      // Ensure have field setting in list default
      if (indexSearchField >= 0) {
        // Get searchWeight from save data
        // If not have => check status = enabled => get default in constant
        //                                       => not have default => set = low
        //                             = disabled => searchWeight = null
        const searchWeight =
          searchFields[indexSearchField].searchWeight && searchFields[indexSearchField].status
            ? searchFields[indexSearchField].searchWeight
            : searchFields[indexSearchField].status
              ? item.searchWeight
                ? item.searchWeight
                : 'low'
              : null;
        return searchWeight
          ? {
              name: searchFields[indexSearchField].name,
              status: searchFields[indexSearchField].status,
              searchWeight
            }
          : {
              name: searchFields[indexSearchField].name,
              status: searchFields[indexSearchField].status
            };
      } else {
        return item;
      }
    });
    result.searchFields = sortingSearchField(searchFieldsSetting);
  }

  if (usingNewTheme) {
    result.inCollectionSearch = {
      ...DEFAULT_SEARCH_SETTINGS.inCollectionSearch,
      ...inCollectionSearch
    };
  }

  result.minimumMustMatch = { ...DEFAULT_SEARCH_SETTINGS.minimumMustMatch, ...minimumMustMatch };

  result.fuzzySearch = [fuzzySearch];

  return result;
};

/**
 * Build data for saving search setting
 *
 * @param  {Object} data search fields data
 * @param  {Object} parseParams parse params from API
 *
 * @return {Object} value value for sending API
 */
export const buildSearchSettingsValues = (data, searchSettings, usingNewTheme = false) => {
  // Convert data

  let res = {
    ...searchSettings,
    searchFields: data.searchFields,
    minimumMustMatch: data.minimumMustMatch,
    fuzzySearch: _.get(data.fuzzySearch, [0], 'true')
  };

  if (usingNewTheme) {
    res.inCollectionSearch = data.inCollectionSearch;
  } else {
    // Force reset enable collection search because at first deployment, In-Collection search also display for theme setup v1
    const enableCollectionSearch = _.get(
      searchSettings,
      'inCollectionSearch.enableCollectionSearch',
      false
    );
    if (enableCollectionSearch) {
      _.set(res, 'inCollectionSearch.enableCollectionSearch', false);
    }
  }

  return res;
};

/**
 * Get label of search field with key in list search field
 *
 * @param  {Object} data search fields data
 * @param  {Object} parseParams parse params from API
 *
 * @return {Object} value value for sending API
 */
export const getLabelSearchField = (searchFields, key) => {
  const name = _.get(searchFields, `[${key}].name`, '');

  return SEARCH_FIELDS_TITLE[name];
};

// Sorting search field list by weight and alphabet of name
const sortingSearchField = searchFields => {
  const highWeightList = searchFields.filter(e => e.searchWeight === 'high');
  const mediumWeightList = searchFields.filter(e => e.searchWeight === 'medium');
  const lowWeightList = searchFields.filter(e => e.searchWeight === 'low');
  const disableList = searchFields.filter(e => !e.status);

  highWeightList.sort((a, b) => a.name.localeCompare(b.name));
  mediumWeightList.sort((a, b) => a.name.localeCompare(b.name));
  lowWeightList.sort((a, b) => a.name.localeCompare(b.name));
  disableList.sort((a, b) => a.name.localeCompare(b.name));

  return [...highWeightList, ...mediumWeightList, ...lowWeightList, ...disableList];
};

/**
 * Check if store is allowed using search v2
 * @param {*} searchVersion
 * @returns
 */
export const allowUsingNewSearchSettings = (searchVersion: string) => {
  return searchVersion && !SEARCH_VERSION.one.includes(searchVersion);
};
/**
 * Get AI search subscription status
 * @param enableAISearch
 * @param statusStore
 * @param addonStatus
 * @returns {string}
 */
export const getAISearchStatus = (
  enableAISearch,
  statusStore,
  addonStatus,
  enabledSubscriptionV5
) => {
  // new pricing v5 check
  if (enabledSubscriptionV5) {
    if (enableAISearch) {
      return SUBSCRIPTION_STATE.UNLOCK;
    }
    return SUBSCRIPTION_STATE.LOCKED;
  }

  // check base on remaining trial days of addon
  const addonRemainingCycles = _.get(addonStatus, 'trialInfo.remainingDays', 14);
  const isSubscribeNow =
    !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0;

  if (enableAISearch) {
    if (!isSubscribeNow && !addonStatus.includedPackage && !_.isEmpty(addonStatus?.addOnInfo)) {
      return SUBSCRIPTION_STATE.TRIAL;
    }
    return SUBSCRIPTION_STATE.UNLOCK;
  }

  if (!isSubscribeNow) {
    return SUBSCRIPTION_STATE.NOT_TRIAL;
  }

  return SUBSCRIPTION_STATE.LOCKED;
};
